import React, { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  CheckCircle, XCircle, Clock, AlertTriangle, Zap, 
  RotateCcw, ChevronRight, Filter, Search, History,
  Play, Pause, DollarSign, Target, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getRiskColor = (risk) => {
  switch (risk) {
    case 'critical': return 'bg-red-500 text-white';
    case 'high': return 'bg-orange-500 text-white';
    case 'medium': return 'bg-yellow-500 text-black';
    case 'low': return 'bg-green-500 text-white';
    default: return 'bg-gray-500 text-white';
  }
};

const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'pending_approval': return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
    case 'rejected': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'failed': return 'bg-red-500/20 text-red-400 border-red-500/30';
    case 'rolled_back': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  }
};

const getActionIcon = (actionType) => {
  if (actionType?.includes('pause')) return <Pause className="w-4 h-4" />;
  if (actionType?.includes('resume')) return <Play className="w-4 h-4" />;
  if (actionType?.includes('budget')) return <DollarSign className="w-4 h-4" />;
  if (actionType?.includes('bid')) return <TrendingUp className="w-4 h-4" />;
  return <Target className="w-4 h-4" />;
};

const ActionCard = ({ action, onApprove, onReject, onRollback, onViewDetails }) => {
  return (
    <Card className="border-border hover:border-primary/30 transition-colors cursor-pointer" onClick={() => onViewDetails(action)}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-lg ${action.status === 'pending_approval' ? 'bg-primary/10' : 'bg-muted'}`}>
              {getActionIcon(action.action_type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-sm">
                  {action.action_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </span>
                <Badge className={getRiskColor(action.risk_level)} variant="outline">
                  {action.risk_level?.toUpperCase()}
                </Badge>
                <Badge className={getStatusColor(action.status)} variant="outline">
                  {action.status?.replace(/_/g, ' ')}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-2 mb-2">
                {action.ai_reasoning}
              </p>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span>Target: {action.target_id}</span>
                <span>Platform: {action.platform_id}</span>
                <span>{new Date(action.created_at).toLocaleString()}</span>
              </div>
            </div>
          </div>
          
          {action.status === 'pending_approval' && (
            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              <Button 
                size="sm" 
                variant="outline" 
                className="text-green-500 hover:bg-green-500/10 border-green-500/30"
                onClick={() => onApprove(action.action_id)}
                data-testid={`approve-${action.action_id}`}
              >
                <CheckCircle className="w-4 h-4 mr-1" /> Approve
              </Button>
              <Button 
                size="sm" 
                variant="outline" 
                className="text-red-500 hover:bg-red-500/10 border-red-500/30"
                onClick={() => onReject(action.action_id)}
                data-testid={`reject-${action.action_id}`}
              >
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
            </div>
          )}
          
          {action.status === 'completed' && (
            <Button 
              size="sm" 
              variant="ghost"
              onClick={(e) => { e.stopPropagation(); onRollback(action.action_id); }}
              data-testid={`rollback-${action.action_id}`}
            >
              <RotateCcw className="w-4 h-4 mr-1" /> Rollback
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export const AIActions = () => {
  const [pendingActions, setPendingActions] = useState([]);
  const [historyActions, setHistoryActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedAction, setSelectedAction] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [actionTypes, setActionTypes] = useState([]);

  const fetchPendingActions = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/actions/pending`, { withCredentials: true });
      setPendingActions(response.data.actions || []);
    } catch (error) {
      console.error('Error fetching pending actions:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const params = statusFilter !== 'all' ? { status: statusFilter } : {};
      const response = await axios.get(`${API}/actions/history`, { 
        params: { ...params, limit: 50 },
        withCredentials: true 
      });
      setHistoryActions(response.data.actions || []);
    } catch (error) {
      console.error('Error fetching action history:', error);
    }
  }, [statusFilter]);

  const fetchActionTypes = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/actions/types`, { withCredentials: true });
      setActionTypes(response.data.action_types || []);
    } catch (error) {
      console.error('Error fetching action types:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchPendingActions(), fetchHistory(), fetchActionTypes()]);
      setLoading(false);
    };
    loadData();
  }, [fetchPendingActions, fetchHistory, fetchActionTypes]);

  useEffect(() => {
    fetchHistory();
  }, [statusFilter, fetchHistory]);

  const handleApprove = async (actionId) => {
    try {
      await axios.post(`${API}/actions/approve`, { 
        action_id: actionId,
        approval_notes: "Approved via Actions dashboard"
      }, { withCredentials: true });
      toast.success('Action approved and executed');
      fetchPendingActions();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve action');
    }
  };

  const handleReject = async (actionId) => {
    try {
      await axios.post(`${API}/actions/reject`, { 
        action_id: actionId,
        rejection_reason: "Rejected via Actions dashboard"
      }, { withCredentials: true });
      toast.success('Action rejected');
      fetchPendingActions();
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject action');
    }
  };

  const handleRollback = async (actionId) => {
    try {
      await axios.post(`${API}/actions/${actionId}/rollback`, {}, { withCredentials: true });
      toast.success('Action rolled back to previous state');
      fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollback action');
    }
  };

  const handleViewDetails = (action) => {
    setSelectedAction(action);
    setDetailsOpen(true);
  };

  const filteredHistory = historyActions.filter(action => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      action.action_type?.toLowerCase().includes(query) ||
      action.target_id?.toLowerCase().includes(query) ||
      action.ai_reasoning?.toLowerCase().includes(query)
    );
  });

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading AI Actions...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-8" data-testid="ai-actions-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">AI Actions</h1>
            <p className="text-muted-foreground">Review and approve AI-recommended campaign actions</p>
          </div>
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="px-3 py-1.5">
              <Clock className="w-4 h-4 mr-2 text-amber-400" />
              {pendingActions.length} Pending
            </Badge>
          </div>
        </div>

        <Tabs defaultValue="pending" className="space-y-6">
          <TabsList>
            <TabsTrigger value="pending" className="flex items-center gap-2" data-testid="tab-pending">
              <Clock className="w-4 h-4" />
              Pending Approval ({pendingActions.length})
            </TabsTrigger>
            <TabsTrigger value="history" className="flex items-center gap-2" data-testid="tab-history">
              <History className="w-4 h-4" />
              Action History
            </TabsTrigger>
          </TabsList>

          {/* Pending Actions Tab */}
          <TabsContent value="pending">
            {pendingActions.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-16">
                  <div className="p-4 bg-muted rounded-full mb-4">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">All Caught Up!</h3>
                  <p className="text-muted-foreground text-center max-w-md">
                    No pending actions awaiting your approval. AI recommendations will appear here when available.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Risk Level Summary */}
                <div className="grid grid-cols-4 gap-4 mb-6">
                  {['critical', 'high', 'medium', 'low'].map(risk => {
                    const count = pendingActions.filter(a => a.risk_level === risk).length;
                    return (
                      <Card key={risk} className={count > 0 ? 'border-primary/30' : ''}>
                        <CardContent className="p-4 flex items-center justify-between">
                          <div>
                            <p className="text-xs text-muted-foreground uppercase">{risk} Risk</p>
                            <p className="text-2xl font-bold">{count}</p>
                          </div>
                          <Badge className={getRiskColor(risk)}>{risk.charAt(0).toUpperCase()}</Badge>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Pending Actions List */}
                <div className="space-y-3">
                  {pendingActions.map(action => (
                    <ActionCard
                      key={action.action_id}
                      action={action}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRollback={handleRollback}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>

                {/* Bulk Actions */}
                {pendingActions.length > 1 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="flex items-center justify-between">
                      <span>You have {pendingActions.length} actions pending. Review each carefully before approval.</span>
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history">
            <div className="space-y-4">
              {/* Filters */}
              <div className="flex items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Search actions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                    data-testid="search-actions"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-48" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                    <SelectItem value="rolled_back">Rolled Back</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* History List */}
              {filteredHistory.length === 0 ? (
                <Card className="border-dashed">
                  <CardContent className="flex flex-col items-center justify-center py-16">
                    <History className="w-8 h-8 text-muted-foreground mb-4" />
                    <h3 className="text-lg font-semibold mb-2">No Action History</h3>
                    <p className="text-muted-foreground">Actions you approve or reject will appear here.</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredHistory.map(action => (
                    <ActionCard
                      key={action.action_id}
                      action={action}
                      onApprove={handleApprove}
                      onReject={handleReject}
                      onRollback={handleRollback}
                      onViewDetails={handleViewDetails}
                    />
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Action Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedAction && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    {getActionIcon(selectedAction.action_type)}
                    {selectedAction.action_type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    <Badge className={getRiskColor(selectedAction.risk_level)}>
                      {selectedAction.risk_level?.toUpperCase()}
                    </Badge>
                  </DialogTitle>
                  <DialogDescription>
                    Action ID: {selectedAction.action_id}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge className={getStatusColor(selectedAction.status)} variant="outline">
                        {selectedAction.status?.replace(/_/g, ' ')}
                      </Badge>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Created</p>
                      <p className="text-sm">{new Date(selectedAction.created_at).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Platform</p>
                      <p className="text-sm font-mono">{selectedAction.platform_id}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Target</p>
                      <p className="text-sm font-mono">{selectedAction.target_id}</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-muted-foreground mb-1">AI Reasoning</p>
                    <p className="text-sm bg-muted p-3 rounded-lg">{selectedAction.ai_reasoning}</p>
                  </div>

                  {selectedAction.parameters && Object.keys(selectedAction.parameters).length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Parameters</p>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(selectedAction.parameters, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedAction.expected_impact && Object.keys(selectedAction.expected_impact).length > 0 && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Expected Impact</p>
                      <pre className="text-xs bg-muted p-3 rounded-lg overflow-x-auto">
                        {JSON.stringify(selectedAction.expected_impact, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedAction.execution_result && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Execution Result</p>
                      <pre className="text-xs bg-green-500/10 p-3 rounded-lg overflow-x-auto text-green-400">
                        {JSON.stringify(selectedAction.execution_result, null, 2)}
                      </pre>
                    </div>
                  )}

                  {selectedAction.approval_notes && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Approval Notes</p>
                      <p className="text-sm bg-muted p-3 rounded-lg">{selectedAction.approval_notes}</p>
                    </div>
                  )}

                  {selectedAction.rejection_reason && (
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">Rejection Reason</p>
                      <p className="text-sm bg-red-500/10 p-3 rounded-lg text-red-400">{selectedAction.rejection_reason}</p>
                    </div>
                  )}
                </div>

                <DialogFooter>
                  {selectedAction.status === 'pending_approval' && (
                    <>
                      <Button variant="outline" className="text-red-500" onClick={() => { handleReject(selectedAction.action_id); setDetailsOpen(false); }}>
                        <XCircle className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      <Button className="bg-green-500 hover:bg-green-600" onClick={() => { handleApprove(selectedAction.action_id); setDetailsOpen(false); }}>
                        <CheckCircle className="w-4 h-4 mr-2" /> Approve
                      </Button>
                    </>
                  )}
                  {selectedAction.status === 'completed' && (
                    <Button variant="outline" onClick={() => { handleRollback(selectedAction.action_id); setDetailsOpen(false); }}>
                      <RotateCcw className="w-4 h-4 mr-2" /> Rollback
                    </Button>
                  )}
                  <DialogClose asChild>
                    <Button variant="ghost">Close</Button>
                  </DialogClose>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default AIActions;
